/* eslint-disable @typescript-eslint/require-await */
import React, {
    FormEvent,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
    TransitionEvent,
    useCallback,
} from "react";
import { Field, Category, ListingItem } from "@lib/shared/types";

import { flatten, uniqBy, cloneDeep } from "@lib/shared/lodash";

import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import {
    getRoutedStepIndex,
    getSkippedStepIndices,
    findNextVisibleIndex,
} from "../utils/stepRouting";
import {
    DomainForm,
    callEvent,
    submitContactUsForm,
    submitLeadForm,
} from "src/api";
import {
    canShowFieldAfterDependency,
    getFirstIncompleteStep,
    handleFieldMasking,
} from "@lib/shared/form";
import { getAccessToken } from "@lib/shared/cookies";
import { SubmitLeadFormResponse } from "src/api/submitLead";
import { useFormContext } from "@hooks/useFormContext";
import { useDomainContext } from "@hooks/useDomainContext";
import { usePrev } from "@hooks/usePrev";
import {
    base64DecodeUnicode,
    base64EncodeUnicode,
    formatPhoneNumber,
    getPhoneNumber,
} from "@lib/sharedUtils";
import { getUserMeta } from "@lib/sharedUtils";
import { useRouter } from "next/router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
    FieldsData,
    UseFormHook,
    FormReducerAction,
    FormSubmitOptions,
} from "../types";
import { useSuccessStep } from "./useSuccessStep";
import { ActionTypes } from "../types";
import { useFields } from "./useFields";
import { useSteps } from "./useSteps";
import { useNext } from "./useNext";
import { formReducer, initState } from "@hooks/useForm/reducer";
import {
    createFormRuntime,
    finalSubmitLock,
    leadIdSingleton,
} from "@hooks/useForm/runtimeState";
import { useOtp } from "./useOtp";
import { useBidding } from "./useBidding";
import { useValidation } from "./useValidation";
import { useNavigation } from "./useNavigation";
import { useFormModals } from "./useFormModals";
import { useSharedFields } from "./useSharedFields";
import { useApiCall } from "./useApiCall";
import { useFieldDataDependency } from "./useFieldDataDependency";
import { useSingleQuestion } from "./useSingleQuestion";
import {
    buildContactUsPayload,
    buildOffersListRedirectUrl,
    buildSecondServiceMeta,
    buildSharedMeta,
    collectFieldsWithValues,
    FieldWithValue,
    fillDefaultAnswersForSkippedQuestions,
    parseRedirectConfig,
} from "@hooks/useForm/sendDataToServer.helpers";

// Module-level page load identifier — regenerated on every hard reload (synchronizedForms only).
const PAGE_LOAD_ID = typeof window !== "undefined" ? `${Date.now()}` : "";

const SYNC_EVENTS = {
    instanceSubmitted: "formInstanceSubmitted",
    stepChanged: "formStepChanged",
    dataSynced: "formDataSynced",
} as const;

const getSyncStorageKeys = (formId: string | number) => ({
    fields: `form-${formId}`,
    submitted: `form-${formId}-submitted`,
    step: `form-${formId}-step`,
    pageLoadId: `form-${formId}-page-load-id`,
});

const safeParseFieldsState = (stateStr: string | null): FieldsData | null => {
    if (!stateStr) return null;
    try {
        return JSON.parse(stateStr) as FieldsData;
    } catch {
        return null;
    }
};

export const useForm = (
    form: DomainForm,
    isSecondServiceForm: boolean | undefined,
    isPiiCompletionForm: boolean | undefined,
    goToListPage: boolean | undefined,
    resetLeadId: boolean,
    scrollToTop: boolean,
    showInModal: boolean,
    isFormInsideOffersModal: boolean,
    isFormSubmitted: React.MutableRefObject<boolean>,
    initialFieldData?: { [key: string]: string },
    singleQuestion?: boolean,
    urlToRedirectFromSingleQuestion?: string,
    isGeneralModalForm?: boolean,
): UseFormHook => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [listingSlug, setListingSlug] = useState<string | null>(null);

    const { asPath, query } = useRouter();
    const { fields } = useFields(form);
    const [showPiiCompletionOtherFields, setShowPiiCompletionOtherFields] =
        useState(false);

    const {
        locale,
        selectedCountryCode,
        totalTimeSpendOnSite,
        domain,
        domainNameTranslation,
        category,
        piiCompletionData,
        handleUpdateOffers,
        // setShowFormModalState,
        setBuyerPhoneNumber,
        apiCallLoading,
        setApiCallLoading,
        loadOneToOneForm,
        routeConfig,
        headerOffer,
        referrer,
        visitDetails,
        userAgent,
    } = useDomainContext();

    const synchronizedForms =
        Boolean(
            routeConfig?.templateConfig?.variantConfig?.synchronizedForms,
        ) && !isFormInsideOffersModal;

    const syncStorageKeys = useMemo(
        () => getSyncStorageKeys(form.id),
        [form.id],
    );

    // Per-instance runtime flags (replaces the previous module-level `let`s).
    // Each call to `useForm` has its own copy so multiple form instances on
    // the same page do not bleed state into each other.
    const runtimeRef = useRef(createFormRuntime()); //need to check this logic more

    // Unique identifier for this form instance — used to prevent self-syncing (synchronizedForms only)
    const instanceIdRef = useRef(
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`,
    );

    //  Used to track whether this instance is the first render of a modal form (synchronizedForms only)
    const [modalFormFirstRender, setModalFormFirstRender] = useState(true);

    // Expose a ref to the function that fetches field data dependencies (synchronizedForms only)
    const fetchSyncedDependenciesRef = useRef<
        ((codeNames: string[]) => void) | null
    >(null);

    const [state, rawDispatch] = useReducer(formReducer, {
        ...initState,
        fieldsState: fields,
    });

    // Wraps the reducer dispatch so side effects that previously lived inside
    // the reducer (clearing the leadId singleton and the per-instance
    // `startSendingData` flag on SET_FORM_SUBMITTED) still happen.
    const dispatch = useCallback((action: FormReducerAction) => {
        if (action.type === ActionTypes.SET_FORM_SUBMITTED) {
            leadIdSingleton.set(null);
            runtimeRef.current.startSendingData = false;
            // Mark — synchronously and globally — that a form has been
            // submitted. This is set the moment ANY useForm instance dispatches
            // SET_FORM_SUBMITTED (including the exit-modal form), so the
            // beforeunload auto-save guard can bail before the page form
            // re-renders to flip its own `isFormSubmitted` ref. Without this,
            // a redirect-triggered `beforeunload` races ahead of that re-render
            // and re-fires a stray `submit: false` request.
            (window as unknown as Record<string, unknown>).__anyFormSubmitted =
                true;
        }
        rawDispatch(action);
    }, []);
    const { steps, setSteps } = useSteps(form, fields, singleQuestion);

    // It's a workaround to keep steps in sync with the current form definition when switching to a second-service form at runtime.
    useEffect(() => {
        if (isSecondServiceForm && fields) {
            const updatedSteps = [...form.steps];
            setSteps(updatedSteps);
        }
    }, [form, isSecondServiceForm, fields]);
    // The step index at which the first API call to fetch buyers should be made. On second-service forms this is always the first step, on regular forms it's determined by the form definition (the `stepToFetchBuyers` field) and compared against the `stepToSendingData` field to ensure it happens before sending data to server.
    const stepToStartFetchingBuyers = useMemo(() => {
        const fetchingBuyersIndex = form.steps.findIndex(
            (step) => step.id === form.stepToFetchBuyers,
        );

        const sendingDataIndex = form.steps.findIndex(
            (step) => step.id === form.stepToSendingData,
        );

        if (fetchingBuyersIndex > sendingDataIndex) {
            return fetchingBuyersIndex;
        }
        return sendingDataIndex;
    }, [form]);

    useEffect(() => {
        // clear leadId on multiform service
        if (state.currentStep === 0 && resetLeadId) {
            const cachedLeadID = sessionStorage.getItem(
                `lead-id-form-${form.id}`,
            );
            leadIdSingleton.set(cachedLeadID ?? null);
            runtimeRef.current.startSendingData = false;
        }
        if (state.submitted && state.currentStep === form.steps.length - 1) {
            setFormSubmitted(true);
            if (synchronizedForms) {
                // Persist submission state so modal forms know on remount
                sessionStorage.setItem(syncStorageKeys.submitted, "true");
                // Notify other same-formId instances that this form was submitted
                window.dispatchEvent(
                    new CustomEvent(SYNC_EVENTS.instanceSubmitted, {
                        detail: {
                            formId: String(form.id),
                            instanceId: instanceIdRef.current,
                        },
                    }),
                );
            }
        }
    }, [state.submitted, state.currentStep, form]);

    const prevFieldsState = usePrev(state.fieldsState);

    const nextState = useNext(state);
    const [windowStep, setWindowStep] = useState<number | null>(null); // window history step
    const successMessage = useSuccessStep(
        form.successMessage,
        state.fieldsState,
        !!isSecondServiceForm,
    );
    const jornayaIdRef = useRef(null);

    const { setFormSubmitted } = useFormContext();

    const getEventDetail = <T,>(event: Event): T =>
        (event as CustomEvent<T>).detail;

    type SyncBaseDetail = {
        formId?: string | number;
        instanceId?: string;
    };

    type SyncStepDetail = SyncBaseDetail & {
        step?: number;
    };

    type SyncDataDetail = SyncBaseDetail & {
        dependentFieldCodeNames?: string[];
    };

    const isPeerEvent = useCallback(
        (detail?: SyncBaseDetail): boolean => {
            return (
                !!detail &&
                String(detail.formId) === String(form.id) &&
                detail.instanceId !== instanceIdRef.current
            );
        },
        [form.id],
    );
    // Submission forms synchronization, Listen for submission events from other form instances with the same formId (synchronizedForms only)
    useEffect(() => {
        if (!synchronizedForms) return;

        const handlePeerSubmission = (event: Event) => {
            const detail = getEventDetail<SyncBaseDetail>(event);
            if (!isPeerEvent(detail) || state.submitted) return;

            dispatch({
                type: ActionTypes.SET_FORM_SUBMITTED,
                payload: { formId: form.id },
            });

            setFormSubmitted(true);
        };

        window.addEventListener(
            SYNC_EVENTS.instanceSubmitted,
            handlePeerSubmission,
        );

        return () => {
            window.removeEventListener(
                SYNC_EVENTS.instanceSubmitted,
                handlePeerSubmission,
            );
        };
    }, [
        synchronizedForms,
        form.id,
        state.submitted,
        dispatch,
        setFormSubmitted,
        isPeerEvent,
    ]);
    // Step synchronization, Listen for step changes (e.g. prev button) from peer form instances (synchronizedForms only)
    useEffect(() => {
        if (!synchronizedForms) return;

        const handlePeerStepChange = (event: Event) => {
            const detail = getEventDetail<SyncStepDetail>(event);

            if (!isPeerEvent(detail) || typeof detail.step !== "number") {
                return;
            }

            dispatch({
                type: ActionTypes.SET_STEP,
                payload: detail.step,
            });
        };

        window.addEventListener(SYNC_EVENTS.stepChanged, handlePeerStepChange);

        return () => {
            window.removeEventListener(
                SYNC_EVENTS.stepChanged,
                handlePeerStepChange,
            );
        };
    }, [synchronizedForms, dispatch, isPeerEvent]);

    // Fields synchronization, Listen for field data sync events from peer form instances and merge the shared field snapshot into state (synchronizedForms only)
    useEffect(() => {
        if (!synchronizedForms) return;

        const handlePeerDataChange = (event: Event) => {
            const detail = getEventDetail<SyncDataDetail>(event);

            if (!isPeerEvent(detail)) return;

            const peerFields = safeParseFieldsState(
                sessionStorage.getItem(syncStorageKeys.fields),
            );

            if (!peerFields) return;

            dispatch({
                type: ActionTypes.SET_FIELDS_STATE,
                payload: peerFields,
            });

            if (detail.dependentFieldCodeNames?.length) {
                fetchSyncedDependenciesRef.current?.(
                    detail.dependentFieldCodeNames,
                );
            }
        };

        window.addEventListener(SYNC_EVENTS.dataSynced, handlePeerDataChange);

        return () => {
            window.removeEventListener(
                SYNC_EVENTS.dataSynced,
                handlePeerDataChange,
            );
        };
    }, [synchronizedForms, dispatch, syncStorageKeys.fields, isPeerEvent]);

    const getPhoneNumberValue = (
        category: Category | null | undefined,
        headerOffer: ListingItem[] | [] | undefined,
    ): string | null => {
        let phoneNumber: string | undefined;
        if (category && headerOffer && headerOffer?.length) {
            phoneNumber = getPhoneNumber(headerOffer[0]);
        } else {
            phoneNumber = "";
        }
        return phoneNumber ? formatPhoneNumber(phoneNumber) : null;
    };

    const phoneNumber = getPhoneNumberValue(category, headerOffer);

    //rejected answer validation: check domain -> check category
    const domainEnableRejectedAnswer = Boolean(
        domain?.config?.enableRejectedAnswers ||
            category?.config?.enableRejectedAnswers,
    );

    const rejectedAnswersRules = domain?.config?.enableRejectedAnswers
        ? domain.config?.rejectedAnswersRules
        : category?.config?.rejectedAnswersRules;

    const jornayaIdElement = useMemo(() => {
        return (
            <input
                id="leadid_token"
                name="universal_leadid"
                type="hidden"
                value=""
                ref={jornayaIdRef}
            />
        );
    }, []);

    const {
        submitted,
        loadingForm,
        currentStep,
        fieldsState,
        formError,
        otp,
        waitingOtp,
        visitedSteps,
        showExitModal,
        stepStatus,
        formErrors,
        formErrorsResolved,
        showRegulations,
        showCustomListingModal,
        showFormModal,
        showFromInExitModal,
        operation,
        globalLoading,
    } = state;

    const skippedStepIndices = useMemo(
        () => getSkippedStepIndices(steps, fieldsState),
        [steps, fieldsState],
    );

    useEffect(() => {
        if (!skippedStepIndices.has(currentStep)) return;
        const nextVisible = findNextVisibleIndex(
            currentStep,
            skippedStepIndices,
            steps.length,
        );
        if (nextVisible === currentStep) return;
        window.history.replaceState(null, "", `#step-${nextVisible + 1}`);
        dispatch({
            type: ActionTypes.SET_STEP,
            payload: nextVisible,
        });
    }, [skippedStepIndices, currentStep, steps.length, dispatch]);

    useEffect(() => {
        const skippedBeforeCurrent = Array.from(skippedStepIndices).filter(
            (index) => index < currentStep,
        );
        if (!skippedBeforeCurrent.length) return;

        const clearedFields: FieldsData = { ...fieldsState };
        let hasChanges = false;

        skippedBeforeCurrent.forEach((index) => {
            const step = steps[index];
            if (!step) return;

            step.fields.forEach((field) => {
                if (field.codeName in clearedFields) {
                    delete clearedFields[field.codeName];
                    hasChanges = true;
                }
            });
        });

        if (!hasChanges) return;

        const replaceFieldsAction: FormReducerAction = {
            type: ActionTypes.REPLACE_FIELDS_STATE,
            payload: clearedFields,
        };

        dispatch(replaceFieldsAction);
    }, [skippedStepIndices, currentStep, steps, fieldsState, dispatch]);

    // Mirror the reducer's `submitted` flag into a ref so the (stable,
    // []-deps) beforeunload listener below can read the *current* submitted
    // state — a plain closure would capture a stale `state.submitted` from the
    // first render. Updated on every render.
    const submittedRef = useRef(false);
    submittedRef.current = state.submitted;

    // Auto-save listener for browser unload/navigation.
    // This persists partially-filled data ONLY when the user leaves WITHOUT
    // completing the form. It must never fire once the form is submitted.
    //
    // Three "already submitted" signals, because no single one covers every
    // case from inside this instance's listener:
    //  - `clickedOnSubmit`: this instance clicked submit.
    //  - `submittedRef`: this instance's reducer is submitted (covers the
    //    inline exit-modal handoff, which marks the page form submitted).
    //  - `__anyFormSubmitted`: set synchronously when ANY instance submits —
    //    covers the exit-modal form (a separate instance) and the redirect ->
    //    beforeunload race that fires before the page form re-renders.
    const sendDataToServerListener = useCallback(() => {
        const accessToken = getAccessToken();
        const anyFormSubmitted = !!(
            window as unknown as Record<string, unknown>
        ).__anyFormSubmitted;
        const willFire =
            !runtimeRef.current.clickedOnSubmit &&
            !submittedRef.current &&
            !isFormSubmitted.current &&
            !anyFormSubmitted &&
            !!accessToken;
        if (willFire) {
            void sendDataToServer({ submit: false });
        }
    }, []);

    const getStepField = (fieldCodeName: string) => {
        const stepIndex = steps.findIndex((s) =>
            s.fields.find((f) => f.codeName === fieldCodeName),
        );

        return steps[stepIndex].fields.find(
            (f) => f.codeName === fieldCodeName,
        );
    };

    // Modal state handlers extracted into a dedicated hook.
    // - toggleShowFormInExitModal: opens/closes the form embedded in the exit-offer modal.
    // - setShowCustomListingModal: controls visibility of the custom listing modal state.
    // - toggleFormModal: toggles the main form-in-modal container based on step/context.
    // - forceShowListingModal: conditionally interrupts normal flow to show a listing modal.
    const {
        toggleShowFormInExitModal,
        setShowCustomListingModal,
        toggleFormModal,
        forceShowListingModal,
    } = useFormModals({ dispatch, setListingSlug });

    // Single-question flow helper.
    // Builds the redirect behavior used by one-question forms, where submit
    // should navigate immediately instead of continuing the standard step flow.
    // `redirectForSingleQuestion` is called from handleSubmit when that mode is enabled.
    const { redirectForSingleQuestion } = useSingleQuestion({
        singleQuestion,
        urlToRedirectFromSingleQuestion,
        dispatch,
        isFormSubmitted,
    });

    const getExcludedBuyersId = (): number[] => {
        if (query.x) {
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const data: { excludedBuyers?: string; redirectedLeadId?: string } =
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                JSON.parse(base64DecodeUnicode(query.x));
            return typeof data.excludedBuyers === "string"
                ? data.excludedBuyers.split(",").map((item) => Number(item))
                : [];
        }
        return [];
    };

    /**
     * Side-channel A: "Partner With Us" / lead-capture form.
     * Bypasses the normal lead flow and posts to the contact-us endpoint.
     */
    const runLeadCaptureSubmit = async (
        fields: FieldWithValue[],
    ): Promise<SubmitLeadFormResponse> => {
        const token = executeRecaptcha
            ? await executeRecaptcha("contact_us_form")
            : null;
        const payload = buildContactUsPayload(fields, {
            googleReCaptcha: token,
            categoryName: category?.name,
        });
        const { error } = await submitContactUsForm({
            //@ts-ignore
            fields: { ...payload },
        });
        if (!error) {
            dispatch({
                type: ActionTypes.SET_FORM_SUBMITTED,
                payload: { formId: form.id },
            });
        }
        return { success: !error, data: null };
    };

    /**
     * Side-channel B: redirect the user to the /offers listing page
     * with their answers encoded into the URL (no lead submission).
     */
    const runGoToListRedirect = (
        fields: FieldWithValue[],
    ): SubmitLeadFormResponse => {
        window.location.href = buildOffersListRedirectUrl(fields, {
            query,
            categorySlugAlias: category?.slugAlias,
        });
        return { success: true, data: null };
    };

    /**
     * Dispatches the reducer action that corresponds to the
     * `submitLeadForm` response — either kick off an operation
     * (round-one / round-two / fetch buyers) or mark the form as
     * fully submitted when the lead was enqueued.
     */
    const dispatchAfterSubmit = ({
        data,
        submit,
        fetchBuyers,
        buyerIds,
    }: {
        data: SubmitLeadFormResponse["data"];
        submit: boolean;
        fetchBuyers: boolean | undefined;
        buyerIds: (number | string)[] | undefined;
    }) => {
        if (data?.operationId) {
            dispatch({
                type: ActionTypes.SET_OPERATION,
                payload: {
                    operationId: data.operationId,
                    key: fetchBuyers
                        ? "FETCH_BUYERS"
                        : submit
                        ? "ROUND_TWO"
                        : "ROUND_ONE",
                },
            });
        } else if (
            submit &&
            (!loadOneToOneForm || buyerIds?.length === 0) &&
            data?.enqueued
        ) {
            dispatch({
                type: ActionTypes.SET_FORM_SUBMITTED,
                payload: { formId: form.id },
            });
        }
    };

    /**
     * Persists the resolved leadId across the leadId singleton,
     * sessionStorage (per-form), and localStorage (`mainId` is the
     * cross-form anchor used by second-service flows).
     */
    const persistLeadId = (data: SubmitLeadFormResponse["data"]) => {
        const nextLeadId = data?.leadId ?? leadIdSingleton.get() ?? null;
        leadIdSingleton.set(nextLeadId);
        if (nextLeadId && resetLeadId) {
            sessionStorage.setItem(`lead-id-form-${form.id}`, nextLeadId);
        }
        if (!isSecondServiceForm) {
            localStorage.setItem("mainId", nextLeadId as string);
        }
    };

    const sendDataToServer = async ({
        submit,
        formOtp,
        skippedValidationFields,
        secondForm,
        consent,
        fetchBuyers,
        buyerIds,
        consentLanguage,
        hasDomainConsent,
    }: {
        submit: boolean;
        formOtp?: string;
        skippedValidationFields?: string[];
        secondForm?: boolean;
        consent?: boolean;
        fetchBuyers?: boolean;
        buyerIds?: (number | string)[];
        consentLanguage?: string;
        hasDomainConsent?: boolean;
    }): Promise<SubmitLeadFormResponse> => {
        let hasSubmitLock = false;

        if (submit) {
            hasSubmitLock = finalSubmitLock.tryAcquire(form.id);
            if (!hasSubmitLock) {
                return { success: false, data: null };
            }
        }

        try {
            // ----- guards -----
            if (singleQuestion) return { success: true, data: null };

            const leadCaptureForm = !!domain.config?.leadCapture;
            if (
                !runtimeRef.current.startSendingData ||
                (leadCaptureForm && !submit) ||
                (goToListPage && !submit)
            ) {
                return { success: false, data: null };
            }

            // ----- mark in-flight -----
            if (submit) runtimeRef.current.clickedOnSubmit = true;
            runtimeRef.current.isApiCallInProgress = true;
            dispatch({ type: ActionTypes.SET_FORM_ERROR, payload: null });

            // ----- collect fields -----
            const { fieldsState: newFieldsState } = await nextState();
            const {
                fields: filledFields,
                selectedOptions: collectedSelectedOptions,
            } = collectFieldsWithValues(newFieldsState, form, {
                isSecondServiceForm: !!isSecondServiceForm,
                leadCaptureForm,
            });
            const {
                fields: fieldsForSubmit,
                autoFilledFieldCodeNames,
                selectedOptions,
            } = fillDefaultAnswersForSkippedQuestions(
                filledFields,
                flatten(steps.map((step) => step.fields)),
                collectedSelectedOptions,
            );
            if (!isSecondServiceForm && state.submitted) {
                return { success: false, data: null };
            }
            if (!fieldsForSubmit.length) return { success: false, data: null };

            // ----- side-channels (early-return flows that bypass submitLeadForm) -----
            if (submit && leadCaptureForm) {
                return runLeadCaptureSubmit(fieldsForSubmit);
            }
            if (submit && goToListPage) {
                return runGoToListRedirect(fieldsForSubmit);
            }

            // ----- main path: submitLeadForm -----
            const redirectConfig = parseRedirectConfig(query.x);
            const sharedMeta = buildSharedMeta({
                selectedCountryCode,
                skippedValidationFields,
                autoFilledFieldCodeNames: submit
                    ? autoFilledFieldCodeNames
                    : undefined,
                selectedOptions,
                timeOnSiteSeconds: totalTimeSpendOnSite.current / 1000,
                consent,
                hasDomainConsent,
                referredByDomain: query.referredBy,
                redirectedLeadId: redirectConfig.redirectedLeadId,
            });
            const secondServiceMeta = buildSecondServiceMeta({
                isSecondService: domain.settings?.isSecondServiceEnabled,
                secondForm,
                categoryName: category?.name,
            });

            const currentLeadId = sessionStorage.getItem(
                `lead-id-form-${form.id}`,
            );
            const forcedBuyersIds = getForcedBuyersIds();
            const excludedBuyers = getExcludedBuyersId();

            const { error, data } = await submitLeadForm({
                fields: fieldsForSubmit,
                leadId:
                    piiCompletionData?.leadId ??
                    leadIdSingleton.get() ??
                    currentLeadId,
                jornayaId:
                    (
                        document?.getElementById(
                            "leadid_token",
                        ) as HTMLInputElement | null
                    )?.value ?? undefined,
                url: window.location.href,
                submit,
                fetchBuyers,
                ...(forcedBuyersIds && fetchBuyers ? { forcedBuyersIds } : {}),
                ...(excludedBuyers.length && fetchBuyers
                    ? { excludedBuyers }
                    : {}),
                domainFormId: form?.domainFormId,
                otpCode: formOtp ?? undefined,
                language: locale ?? "en",
                consentLanguage,
                meta: { ...getUserMeta(), ...secondServiceMeta, ...sharedMeta },
                isPiiCompletionForm: isPiiCompletionForm || undefined,
                token: isPiiCompletionForm
                    ? (query["pii-token"] as string)
                    : undefined,
                buyerIds,
                isOneToOneConsent: !!loadOneToOneForm,
            });

            // ----- post-response -----
            dispatchAfterSubmit({ data, submit, fetchBuyers, buyerIds });
            persistLeadId(data);
            // drain next queued task if any
            if (runtimeRef.current.queues.length > 0) {
                const queued = runtimeRef.current.queues.shift();
                if (queued) {
                    await queued();
                }
            }

            return { data, success: !error };
        } finally {
            runtimeRef.current.isApiCallInProgress = false;
            if (hasSubmitLock) {
                finalSubmitLock.release(form.id);
            }
        }
    };
    const setLoading = (loading: boolean) => {
        dispatch({ type: ActionTypes.SET_LOADING_FORM, payload: loading });
    };
    const setLoadingBuyers = (loading: boolean) => {
        dispatch({
            type: ActionTypes.SET_OPERATION,
            payload: {
                loadingBuyers: loading,
            },
        });
    };
    const setHasRegulations = (hasRegulations: boolean) => {
        dispatch({
            type: ActionTypes.SHOW_REGULATIONS_PAGE,
            payload: { showRegulations: hasRegulations },
        });
    };
    /**
     * Flatten every step's fields into a single `{ codeName: value }` map,
     * keeping only entries the user actually filled (empty / whitespace-only
     * values are dropped). Used to pre-fill the fallback funnel.
     */
    const collectFilledFieldValues = (): { [codeName: string]: string } => {
        const filled: { [codeName: string]: string } = {};
        form.steps.forEach((step) => {
            step.fields.forEach((f) => {
                const value = fieldsState[f.codeName]?.value;
                if (value?.trim()) {
                    filled[f.codeName] = value;
                }
            });
        });
        return filled;
    };

    /**
     * Wipe the cached form state for this form on this domain so the user
     * can't come back and resubmit the same rejected lead.
     */
    const clearFormCacheForCurrentDomain = () => {
        sessionStorage.removeItem(`form-${form.id}`);
        sessionStorage.removeItem(`lead-id-form-${form.id}`);
    };

    /**
     * Resolve the environment-matching subdomain prefix so the fallback URL
     * stays in the same environment as the current page
     * (staging → staging.<fallback>, local → local.<fallback>, else prod).
     */
    const getEnvironmentSubdomain = (): string => {
        const hostname = window.location.hostname;
        const isStaging = hostname.includes("staging");
        const isLocal = hostname.includes("local.");
        if (isStaging) return "staging.";
        if (isLocal) return "local.";
        return "";
    };

    /**
     * Build the absolute URL that hands the rejected lead off to the fallback
     * funnel, encoding the user's answers and the buyer-exclusion payload as
     * query params.
     */
    const buildFallbackRedirectUrl = (filledFields: {
        [codeName: string]: string;
    }): string => {
        // `la` param — base64-encoded answers consumed by the fallback funnel
        // to pre-fill its own fields.
        const encodedAnswers = base64EncodeUnicode(
            JSON.stringify(filledFields),
        );

        // `x` param — base64-encoded payload telling the fallback funnel
        // which buyers already rejected this lead (so it doesn't offer them
        // again) and the original leadId so the back-end can link both attempts.
        const excludedBuyers =
            domain.fallbackSettings?.excludedBuyers.join(",");
        const encodedRedirectInfo = base64EncodeUnicode(
            JSON.stringify({
                excludedBuyers,
                redirectedLeadId: leadIdSingleton.get() || "",
            }),
        );

        // Forward marketing / tracking query params, but strip the ones we're
        // about to overwrite (`x`, `la`, `tb`, `lead_uuid`) and the legacy
        // `data` param.
        const STRIPPED_QUERY_PARAMS = ["data", "la", "tb", "x", "lead_uuid"];
        const forwardedQueryString = gettingQueriesAsString(
            query,
            "start",
            STRIPPED_QUERY_PARAMS,
        );

        // `domain.fallbackSettings.domain` may be stored with or without the
        // https:// prefix; normalize so we don't end up with `https://https://`.
        const rawDestination = domain.fallbackSettings?.domain as string;
        const cleanedDestination = rawDestination.includes("https://")
            ? rawDestination.split("https://")[1]
            : rawDestination;

        // `/fbas` (auto-sell) vs `/fb` (regular fallback). `tb=true` flags
        // this as a transferred-buyer / rejected-lead handoff so the
        // destination knows to consume the encoded payload.
        const redirectPath = domain.config?.autoSell ? "fbas" : "fb";
        const currentLeadId = leadIdSingleton.get();
        const leadUuidParam = currentLeadId
            ? `&lead_uuid=${currentLeadId}`
            : "";

        const protocol = window.location.protocol;
        const port = window.location.port;
        const portSegment = port ? `:${port}` : "";
        const subdomain = getEnvironmentSubdomain();

        return (
            `${protocol}//${subdomain}${cleanedDestination}${portSegment}` +
            `/${redirectPath}` +
            `?x=${encodedRedirectInfo}` +
            `&tb=true` +
            `&la=${encodedAnswers}` +
            `${leadUuidParam}` +
            `&${forwardedQueryString}`
        );
    };

    /**
     * Called when the current lead is rejected (no buyer accepts it).
     * Forwards the user to the domain configured in `domain.fallbackSettings`
     * (a different brand/funnel under the same family) while preserving the
     * answers they already filled in, so they don't have to start over.
     */
    const handleRejectedLeads = () => {
        const filledFields = collectFilledFieldValues();
        clearFormCacheForCurrentDomain();
        const fallbackUrl = buildFallbackRedirectUrl(filledFields);
        window.location.replace(fallbackUrl);
    };

    const {
        fetchFirstRoundBuyers,
        startFirstRoundBidding,
        startSecondRoundBidding,
    } = useBidding({
        form,
        domain,
        dispatch,
        sendDataToServer,
        setLoading,
        setLoadingBuyers,
        handleRejectedLeads,
        categorySlug: category?.slug,
        initialFieldData,
    });

    const { saveSharedFields, fillFormWithSharedFields, getSharedFields } =
        useSharedFields({ dispatch });

    const { serverSideValidation, handleFieldBlur, handleStepValidation } =
        useValidation({
            form,
            domain,
            locale,
            selectedCountryCode,
            domainNameTranslation,
            state,
            steps,
            dispatch,
            runtimeRef,
            otp,
            isPiiCompletionForm,
            piiCompletionData,
            loadingForm,
            handleUpdateOffers,
            getStepField,
            nextState,
            saveSharedFields,
            sendDataToServer,
            setHasRegulations,
            setShowPiiCompletionOtherFields,
            domainEnableRejectedAnswer,
            rejectedAnswersRules,
            phoneNumber, // for rejected answer rules
            referrer, // for rejected answer rules
            visitDetails, // for rejected answer rules
            userAgent, // for rejected answer rules
        });

    const dispatchNextStep = (options?: FormSubmitOptions) => {
        const routedStep = getRoutedStepIndex(
            steps,
            currentStep,
            options?.routingFieldsData ?? fieldsState,
        );
        let nextStep: number;
        if (routedStep !== null) {
            window.history.pushState(null, "", `#step-${routedStep + 1}`);
            dispatch({
                type: ActionTypes.SET_STEP,
                payload: routedStep,
            });
            nextStep = routedStep;
        } else {
            dispatch({
                type: ActionTypes.SET_NEXT_STEP,
                payload: undefined,
            });
            nextStep = currentStep + 1;
        }
        if (synchronizedForms) {
            // Notify peer form instances about the step advance.
            window.dispatchEvent(
                new CustomEvent(SYNC_EVENTS.stepChanged, {
                    detail: {
                        formId: String(form.id),
                        instanceId: instanceIdRef.current,
                        step: nextStep,
                    },
                }),
            );
        }
    };
    const handleSubmit = async (
        ev?: FormEvent<HTMLFormElement>,
        options?: FormSubmitOptions,
    ) => {
        ev?.preventDefault();

        // Guard against duplicate submissions / form already finalized.
        const isFormBusy =
            loadingForm ||
            submitted ||
            waitingOtp ||
            finalSubmitLock.isLocked(form.id);
        if (isFormBusy) return;

        runtimeRef.current.submitStepClicked = true;

        const stepValid = options?.skipValidation
            ? false
            : await handleStepValidation(options?.routingFieldsData);

        // We can advance when the step validated OR the caller explicitly
        // asked us to skip validation (e.g. silent / programmatic advance).
        const canProceed = stepValid || !!options?.skipValidation;

        // Single-question forms have their own redirect flow and never run
        // through the normal step-by-step submission below.
        if (singleQuestion && canProceed) {
            redirectForSingleQuestion({ skipToStep: options?.skipToStep });
            return;
        }

        // Fire the analytics "first form interaction" event once when the
        // user submits step 0.
        if (currentStep === 0) {
            void callEvent({
                event: "FirstFormInteraction",
            });
        }

        // ----- local flags shared by the helpers below -----
        const isFirstStep = currentStep === 0;
        const isLastStep = currentStep === steps.length - 1;
        const hasTcpaOnCurrentStep = !!steps[currentStep]?.includeTCPAMessage;
        const shouldFetchBuyers =
            !!loadOneToOneForm && currentStep === stepToStartFetchingBuyers;
        const isSecondFormFlag = !!options?.isSecondForm;

        // The hidden <label> rendered next to the TCPA checkbox; we capture
        // its rendered HTML so the back-end can store the exact consent text
        // the user agreed to.
        const tcpaText = document.querySelector(
            '[data-tf-element-role="consent-language"]',
        ) as HTMLLabelElement;

        // Buyers the user picked in the round-one bidding modal (only set on
        // one-to-one flows).
        const selectedBuyerIds = operation?.firstRoundBuyers
            ?.filter((i) => i.selected)
            ?.map((buyer) => buyer.buyerId);

        // ----- TCPA #1: record consent (+ optionally fetch buyers) BEFORE
        // the optional first-step api-call flow runs, so consent is captured
        // even if that flow short-circuits the rest of handleSubmit.
        if (hasTcpaOnCurrentStep) {
            void sendDataToServer({
                submit: false,
                consent: true,
                secondForm: options?.isSecondForm,
                fetchBuyers: shouldFetchBuyers,
                consentLanguage: tcpaText?.innerHTML,
                buyerIds: selectedBuyerIds,
                hasDomainConsent: operation?.hasDomainConsent,
            });
        }

        // First-step api-call hook (e.g. instant-quote / phone-number lookup).
        // If it returns a buyer phone number we hand that to the UI and stop.
        const shouldRunApiCallFlow =
            !!options?.enableApiCall && canProceed && isFirstStep;
        if (shouldRunApiCallFlow) {
            const result = await applyApiCallFlow(options);
            if (result) {
                setBuyerPhoneNumber(result);
                setApiCallLoading(false);
                return;
            }
        }

        // ----- TCPA #2: re-record consent after the api-call flow ran.
        // Buyers were already requested in TCPA #1 so this call omits
        // fetchBuyers/buyerIds on purpose.
        if (hasTcpaOnCurrentStep) {
            void sendDataToServer({
                submit: false,
                consent: true,
                secondForm: options?.isSecondForm,
            });
        }

        if (!canProceed) return;

        // ----- step-progress / lead-submission helpers (closures over the
        // locals declared above; intentionally inner so we don't have to
        // thread tcpaText / selectedBuyerIds / options through arguments).

        const advanceToNextStep = () => {
            dispatchNextStep(options);
        };

        /** Final-step submit: send the lead and run server-side validation. */
        const submitFinalStep = async () => {
            const { data, success } = await sendDataToServer({
                submit: true,
                skippedValidationFields:
                    runtimeRef.current.skippedValidationFields,
                secondForm: isSecondFormFlag,
                buyerIds: selectedBuyerIds,
                consentLanguage: tcpaText?.innerHTML,
            });
            serverSideValidation({ data, success });
        };

        /** Intermediate-step send: just persists step data (no submit). */
        const sendIntermediateStepData = (fetchBuyersFlag: boolean) =>
            sendDataToServer({
                submit: false,
                skippedValidationFields:
                    runtimeRef.current.skippedValidationFields,
                secondForm: isSecondFormFlag,
                fetchBuyers: fetchBuyersFlag,
            });

        /**
         * Creates the lead (no leadId yet) and either runs final validation
         * on submit or advances to the next step.
         */
        const createLeadAndContinue = async (fetchBuyersFlag: boolean) => {
            const { data, success } = await sendDataToServer({
                submit: isLastStep,
                skippedValidationFields:
                    runtimeRef.current.skippedValidationFields,
                secondForm: isSecondFormFlag,
                fetchBuyers: fetchBuyersFlag,
                buyerIds: isLastStep ? selectedBuyerIds : undefined,
            });

            if (isLastStep) {
                serverSideValidation({ data, success });
            } else {
                advanceToNextStep();
            }
        };

        // `zipCode` is cached by the StreetAddress field's autocomplete;
        // clear it so it doesn't leak between forms / submissions.
        localStorage.removeItem("zipCode");

        const hasExistingLeadId = !!leadIdSingleton.get();
        const isAnotherApiCallInFlight = runtimeRef.current.isApiCallInProgress;

        // ----- no lead yet: create one (optionally queued behind an
        // in-flight call). startLoadingForm so the UI shows a spinner while
        // we wait for the lead to be created.
        if (!hasExistingLeadId) {
            setLoading(true);
            if (isAnotherApiCallInFlight) {
                runtimeRef.current.queues.push(async () => {
                    void createLeadAndContinue(shouldFetchBuyers);
                });
            } else {
                void createLeadAndContinue(shouldFetchBuyers);
            }
            return;
        }

        // ----- lead already exists -----

        if (isAnotherApiCallInFlight) {
            // Queue the step-data insertion behind the in-flight call so
            // requests don't race.
            if (isLastStep) {
                setLoading(true);
                runtimeRef.current.queues.push(submitFinalStep);
            } else {
                if (shouldFetchBuyers) {
                    runtimeRef.current.queues.push(async () => {
                        await sendIntermediateStepData(true);
                    });
                }
                // Don't wait for the queued call \u2014 advance the UI now so
                // the user perceives the step transition as instant.
                advanceToNextStep();
            }
            return;
        }

        // ----- no api call in flight, lead exists \u2014 fire immediately -----
        if (isLastStep) {
            setLoading(true);
            await submitFinalStep();
        } else {
            void sendIntermediateStepData(shouldFetchBuyers);
            advanceToNextStep();
        }
    };
    const loadFieldsFromSessionStorage = (): FieldsData | null => {
        try {
            if (initialFieldData) {
                const fieldData = {
                    valid: true,
                    errorMessage: null,
                    value: null,
                    optionSelectedIndex: undefined,
                    changed: false,
                    fieldType: "email",
                    defaultValues: null,
                };
                const x = Object.keys(initialFieldData).reduce(
                    (acc: { [key: string]: any }, key) => {
                        acc[key] = {
                            ...fieldData,
                            value: initialFieldData[key],
                        };
                        return acc;
                    },
                    {},
                );
                return x;
            }

            if (isPiiCompletionForm) {
                const fields =
                    piiCompletionData?.fields.reduce((acc, field) => {
                        acc[field.codeName] = {
                            value: field.value,
                            errorMessage: "",
                            changed: false,
                            valid: true,
                        };
                        return acc;
                    }, {} as FieldsData) ?? null;
                return fields;
            }
            const stateStr = sessionStorage.getItem(`form-${form.id}`);

            return stateStr ? (JSON.parse(stateStr) as FieldsData) : null;
        } catch (e) {
            return null;
        }
    };

    const reloadFormData = useCallback(() => {
        const sessionStorageFields = loadFieldsFromSessionStorage();
        if (!sessionStorageFields) return;
        // Fix optionSelectedIndex for radio fields that were converted to select in modal
        const allFields = flatten(steps.map((step) => step.fields));
        const fieldByCodeName = new Map(
            allFields.map((field) => [field.codeName, field]),
        );
        const fixedFields: FieldsData = { ...sessionStorageFields };

        // Restore missing optionSelectedIndex for radio values saved from modal/select rendering.
        Object.keys(fixedFields).forEach((codeName) => {
            const fieldDef = fieldByCodeName.get(codeName);
            const fieldData = fixedFields[codeName];
            const isMissingSelectedIndex =
                fieldData.optionSelectedIndex === undefined ||
                fieldData.optionSelectedIndex === null ||
                fieldData.optionSelectedIndex === "";
            // If this is a radio field and has a value but no optionSelectedIndex
            if (
                fieldDef?.fieldType !== "radio" ||
                !fieldData?.value ||
                !isMissingSelectedIndex
            ) {
                return;
            }

            const options =
                fieldDef.defaultValues?.options ||
                fieldData.defaultValues?.options;
            const selectedIndex = options?.findIndex(
                (option) => option.value === fieldData.value,
            );

            if (selectedIndex === undefined || selectedIndex < 0) return;

            fixedFields[codeName] = {
                ...fieldData,
                optionSelectedIndex: `${codeName}-${selectedIndex}`,
            };
        });

        const resolveFirstIncompleteStep =
            getFirstIncompleteStep as unknown as (
                formSteps: typeof steps,
                currentFieldsState: FieldsData,
            ) => number;

        const targetStep: number = resolveFirstIncompleteStep(
            steps,
            fixedFields,
        );

        dispatch({
            type: ActionTypes.SET_FIELDS_STATE,
            payload: fixedFields,
        });
        dispatch({
            type: ActionTypes.SET_STEP,
            payload: targetStep,
        });
        // Update URL hash to reflect the target step
        window.history.pushState("", document.title, `#step-${targetStep + 1}`);
        // Also update sessionStorage with the fixed data
        sessionStorage.setItem(`form-${form.id}`, JSON.stringify(fixedFields));
        // Also update localStorage globalFormValues immediately
        if (!isSecondServiceForm) {
            const formAnswers = Object.keys(fixedFields).reduce((acc, key) => {
                acc[key] = fixedFields[key].value;
                return acc;
            }, {} as { [key: string]: string | null });
            // @ts-ignore
            window.formAnswers = formAnswers;
            localStorage.setItem(
                "globalFormValues",
                JSON.stringify(formAnswers),
            );
        }
    }, [
        form.id,
        initialFieldData,
        isPiiCompletionForm,
        isSecondServiceForm,
        steps,
    ]);

    const { getFieldsDataDependency, fetchFieldsDataDependency } =
        useFieldDataDependency({
            steps,
            setSteps,
            prevFieldsState,
            fieldsState,
            nextState,
        });
    // fetch data dependencies for fields that were updated in peer instances of the same form.(synchronizedForms only)
    useEffect(() => {
        if (!synchronizedForms) return;
        fetchSyncedDependenciesRef.current = (codeNames: string[]) => {
            const allFields = flatten(steps.map((step) => step.fields));
            const fieldsToFetch = allFields.filter(
                (f) => codeNames.includes(f.codeName) && f.dataDependency,
            );
            if (fieldsToFetch.length) {
                void fetchFieldsDataDependency(fieldsToFetch);
            }
        };
    }, [steps, fetchFieldsDataDependency]);

    const { applyApiCallFlow } = useApiCall({
        fieldsState,
        dispatch,
        setApiCallLoading,
        setBuyerPhoneNumber,
        category,
    });

    const handleFieldChange = async (
        field: Field,
        val: string,
        optionSelectedIndex: string | undefined,
    ) => {
        runtimeRef.current.submitStepClicked = false;
        let newVal = val;

        if (field.mask) {
            if (field.mask === "zipCode" && selectedCountryCode === "CA") {
                newVal = handleFieldMasking("postalCode", val);
            } else {
                newVal = handleFieldMasking(field.mask, val);
            }
        }

        const newObj: FieldsData = {};

        // Update field value
        newObj[field.codeName] = {
            ...field,
            valid: true,
            errorMessage: "",
            value: newVal,
            changed: true,
        };

        if (optionSelectedIndex) {
            newObj[field.codeName]["optionSelectedIndex"] = optionSelectedIndex;
        }

        const { fieldsWithDataDependencyAndDependentOn } =
            getFieldsDataDependency({
                changedFields: newObj,
                checkChangedFields: true,
            });

        // Get all fields that depends on the field that value changed
        const finalChangedFields = [...fieldsWithDataDependencyAndDependentOn];

        // Get all fields that depends on the depends field that value changed
        fieldsWithDataDependencyAndDependentOn.forEach(
            (fieldWithDataDependency) => {
                const { fieldsWithDataDependencyAndDependentOn: temp } =
                    getFieldsDataDependency({
                        checkChangedFields: true,
                        // reset value to empty string
                        changedFields: {
                            [fieldWithDataDependency.codeName]: {
                                value: "",
                            },
                        },
                    });

                newObj[fieldWithDataDependency.codeName] = {
                    ...fieldWithDataDependency,
                    valid: true,
                    errorMessage: "",
                    value: "",
                    changed: true,
                };

                temp.forEach((i) => {
                    newObj[i.codeName] = {
                        ...i,
                        valid: true,
                        errorMessage: "",
                        value: "",
                        changed: true,
                    };
                });

                finalChangedFields.push(...temp);
            },
        );

        // update state for all dependingOn fields
        dispatch({
            type: ActionTypes.SET_FIELDS_STATE,
            payload: { ...newObj },
        });

        void fetchFieldsDataDependency(uniqBy(finalChangedFields, "codeName"));

        const { fieldsState: newFieldsState } = await nextState();

        sessionStorage.setItem(
            `form-${form.id}`,
            JSON.stringify({ ...newFieldsState, ...newObj }),
        );

        // Notify other form instances about data update
        if (isFormInsideOffersModal) {
            // Also update localStorage globalFormValues for modal form
            if (!isSecondServiceForm) {
                const allFieldsState = { ...newFieldsState, ...newObj };
                const formAnswers = Object.keys(allFieldsState).reduce(
                    (res, key) => {
                        res = {
                            ...res,
                            [key]: allFieldsState[key].value,
                        };
                        return res;
                    },
                    {},
                );
                localStorage.setItem(
                    "globalFormValues",
                    JSON.stringify(formAnswers),
                );
            }

            window.dispatchEvent(
                new CustomEvent("formDataUpdated", {
                    detail: { formId: form.id },
                }),
            );
        }

        // Notify peer instances about the changed fields so they can update accordingly
        if (synchronizedForms) {
            window.dispatchEvent(
                new CustomEvent(SYNC_EVENTS.dataSynced, {
                    detail: {
                        formId: String(form.id),
                        instanceId: instanceIdRef.current,
                        dependentFieldCodeNames: uniqBy(
                            finalChangedFields,
                            "codeName",
                        ).map((f) => f.codeName),
                    },
                }),
            );
        }

        if (
            (field.fieldType === "radio" ||
                (field.fieldType === "select" && singleQuestion)) &&
            !forceShowListingModal({
                meta: field.meta,
                selectedValue: val,
                currentFieldCodeName: field.codeName,
            })
        ) {
            if (checkIfStepHasOneField(newObj)) {
                void handleSubmit(undefined, {
                    skipValidation: field.meta?.rejectedAnswers ? false : true, // in case of radio, check if there is rejected answer meta configured, if yes then validate the step otherwise skip step validation
                    skipToStep: val ? "1" : undefined,
                    routingFieldsData: {
                        ...fieldsState,
                        ...newObj,
                    },
                });
            }
        }
    };

    const checkIfStepHasOneField = (fieldsData: FieldsData) => {
        if (
            steps[currentStep]?.fields.length === 1 &&
            (steps[currentStep]?.fields[0].fieldType === "radio" ||
                (steps[currentStep]?.fields[0].fieldType === "select" &&
                    singleQuestion))
        ) {
            return true;
        }

        let numberOfFields = 0;
        let stepHasOneRadioField = false;

        steps[currentStep].fields.forEach((field) => {
            if (canShowFieldAfterDependency(field, fieldsData)) {
                numberOfFields++;
                stepHasOneRadioField =
                    field.fieldType === "radio" && !stepHasOneRadioField
                        ? true
                        : false;
            }
        });

        return numberOfFields === 1 && stepHasOneRadioField;
    };

    const {
        handlePrevStep,
        handleWindowBack,
        handleFirstStepFieldFocus,
        handleScrollFormTop,
    } = useNavigation({
        form,
        state,
        dispatch,
        setWindowStep,
        skippedStepIndices,
        instanceId: instanceIdRef.current,
        isGeneralModalForm,
        synchronizedForms,
    });

    const handleOutsideFormSubmit = () => {
        dispatch({
            type: ActionTypes.SET_FORM_SUBMITTED,
            payload: { formId: form.id },
        });
    };

    const { handleOtpSubmit, handleOtpResend, handleOtpEditPhoneNumber } =
        useOtp({
            state,
            dispatch,
            sendDataToServer,
            setLoading,
        });
    function onTransitionEnd(animation: TransitionEvent): void {
        const eventTarget = animation.target as HTMLElement;
        if (
            animation.propertyName === "transform" &&
            eventTarget?.id === "stepsWrapper"
        ) {
            handleFirstStepFieldFocus();
        }
    }

    useEffect(() => {
        if (isSecondServiceForm) {
            const fetchSharedFields = async () => {
                const sharedFields = await getSharedFields();
                if (sharedFields) {
                    fillFormWithSharedFields(sharedFields);
                }
            };
            void fetchSharedFields();
        }
    }, []);

    const handleCloseFormInsideExitModal = (options?: {
        wasFormSubmitted?: boolean;
    }) => {
        // If called because the modal form was submitted, mark the main form as submitted too
        if (options?.wasFormSubmitted) {
            toggleShowFormInExitModal(false);

            // Set the hero form to the last step so the slide animation works correctly
            // (useSlideFormValue calculates thank you position relative to currentStep)
            dispatch({
                type: ActionTypes.SET_STEP,
                payload: steps.length - 1,
            });

            dispatch({
                type: ActionTypes.SET_FORM_SUBMITTED,
                payload: { formId: form.id },
            });
            return;
        }

        if (!state.blockBack) {
            const sessionStorageFields = loadFieldsFromSessionStorage();
            const { steps } = form;

            if (sessionStorageFields) {
                // Fix optionSelectedIndex for radio fields that were converted to select in modal
                const allFields = flatten(steps.map((step) => step.fields));
                const fixedFields = { ...sessionStorageFields };

                Object.keys(fixedFields).forEach((codeName) => {
                    const fieldDef = allFields.find(
                        (f) => f.codeName === codeName,
                    );
                    const fieldData = fixedFields[codeName];

                    // If this is a radio field and has a value but no optionSelectedIndex
                    if (
                        fieldDef?.fieldType === "radio" &&
                        fieldData?.value &&
                        (fieldData.optionSelectedIndex === undefined ||
                            fieldData.optionSelectedIndex === null ||
                            fieldData.optionSelectedIndex === "")
                    ) {
                        // Get options from either defaultValues or field data itself
                        const options =
                            fieldDef.defaultValues?.options ||
                            fieldData.defaultValues?.options;

                        // Find the index of the selected option
                        const selectedIndex = options?.findIndex(
                            (option) => option.value === fieldData.value,
                        );

                        if (selectedIndex !== undefined && selectedIndex >= 0) {
                            // Format must match: `${field.codeName}-${index}`
                            fixedFields[codeName] = {
                                ...fieldData,
                                optionSelectedIndex: `${codeName}-${selectedIndex}`,
                            };
                        }
                    }
                });

                dispatch({
                    type: ActionTypes.SET_FIELDS_STATE,
                    payload: cloneDeep(fixedFields),
                });

                // Also update sessionStorage with the fixed data
                sessionStorage.setItem(
                    `form-${form.id}`,
                    JSON.stringify(fixedFields),
                );
            }

            const { fieldsWithDataDependencyAndDependentOn } =
                getFieldsDataDependency({
                    checkChangedFields: false,
                    changedFields: {},
                });

            void fetchFieldsDataDependency(
                fieldsWithDataDependencyAndDependentOn,
            );

            // Find the first step with unfilled required fields
            let targetStep = steps.length - 1; // Default to last step if all complete
            for (let i = 0; i < steps.length; i++) {
                const requiredFields = steps[i].fields.filter(
                    (field) =>
                        field.isRequired === true &&
                        canShowFieldAfterDependency(
                            field,
                            sessionStorageFields,
                        ),
                );

                const allRequiredFieldsFilled = requiredFields.every(
                    (field) =>
                        sessionStorageFields?.[field.codeName]?.value &&
                        sessionStorageFields?.[field.codeName]?.valid === true,
                );

                if (!allRequiredFieldsFilled) {
                    targetStep = i;
                    break;
                }
            }

            dispatch({
                type: ActionTypes.SET_STEP,
                payload: targetStep,
            });
            window.history.pushState(null, "", `#step-${targetStep + 1}`);
            setWindowStep(targetStep + 1);
        }
        toggleShowFormInExitModal(false);
    };

    useEffect(() => {
        if (synchronizedForms) return; // to avoid unwanted scroll when forms are synchronized and step changes are triggered by peer forms
        const animated = document.querySelector("#stepsWrapper");
        if (animated) {
            animated.addEventListener(
                "transitionend" as keyof ElementEventMap,
                onTransitionEnd,
                false,
            );
        }
        return () => {
            if (animated) {
                animated.removeEventListener(
                    "transitionend" as keyof ElementEventMap,
                    onTransitionEnd,
                    false,
                );
            }
        };
    }, []);

    useEffect(() => {
        setModalFormFirstRender(false);
        if (currentStep === 0 && showFormModal) {
            toggleFormModal(false);
        } else if (
            currentStep > 0 &&
            (form.testConfig?.config?.formInsideModal || showInModal)
        ) {
            toggleFormModal(true);
        }
        setTimeout(() => {
            if (asPath.split("/")[1] !== "blog" && scrollToTop) {
                handleScrollFormTop();
            }
        }, 50);

        if (!isGeneralModalForm && synchronizedForms) {
            sessionStorage.setItem(syncStorageKeys.step, String(currentStep));
        }

        if (isGeneralModalForm && synchronizedForms && !modalFormFirstRender) {
            sessionStorage.setItem(syncStorageKeys.step, String(currentStep));
        }

        if (!form.stepToSendingData) {
            runtimeRef.current.startSendingData = true;
        } else if (steps[currentStep]?.id === form.stepToSendingData) {
            runtimeRef.current.startSendingData = true;
        }
    }, [currentStep]);

    const moveBack = async () => {
        const { blockBack } = await nextState();
        if (
            windowStep &&
            windowStep - 1 < currentStep &&
            !submitted &&
            !otp.isRequired &&
            !blockBack
        ) {
            dispatch({
                type: ActionTypes.SET_STEP,
                payload: windowStep - 1,
            });
        }
    };

    useEffect(() => {
        void moveBack();
    }, [windowStep]);
    useEffect(() => {
        if (currentStep === 1 && apiCallLoading) {
            setApiCallLoading(false);
        }
    }, [currentStep, apiCallLoading, setApiCallLoading]);

    useEffect(() => {
        const asyncFunction = async () => {
            const sessionStorageFields = loadFieldsFromSessionStorage();
            if (initialFieldData) {
                dispatch({
                    type: ActionTypes.SET_GLOBAL_LOADING,
                    payload: true,
                });
                void sendDataToServer({
                    submit: false,
                    fetchBuyers: true,
                });
            } else {
                dispatch({
                    type: ActionTypes.SET_GLOBAL_LOADING,
                    payload: false,
                });
            }

            if (sessionStorageFields) {
                dispatch({
                    type: ActionTypes.SET_FIELDS_STATE,
                    payload: { ...sessionStorageFields },
                });
            }

            const { fieldsWithDataDependencyAndDependentOn } =
                getFieldsDataDependency({
                    checkChangedFields: false,
                    changedFields: {},
                });

            void fetchFieldsDataDependency(
                fieldsWithDataDependencyAndDependentOn,
            );

            if (!isGeneralModalForm && synchronizedForms) {
                sessionStorage.removeItem(syncStorageKeys.submitted);
                sessionStorage.setItem(
                    syncStorageKeys.pageLoadId,
                    PAGE_LOAD_ID,
                );
            }

            if (isGeneralModalForm && synchronizedForms) {
                const storedPageLoadId = sessionStorage.getItem(
                    syncStorageKeys.pageLoadId,
                );

                if (storedPageLoadId !== PAGE_LOAD_ID) {
                    sessionStorage.removeItem(syncStorageKeys.submitted);
                    sessionStorage.removeItem(syncStorageKeys.step);
                    sessionStorage.setItem(
                        syncStorageKeys.pageLoadId,
                        PAGE_LOAD_ID,
                    );
                }

                setTimeout(() => {
                    // Check if form was already submitted
                    const wasSubmitted =
                        sessionStorage.getItem(syncStorageKeys.submitted) ===
                        "true";

                    if (wasSubmitted) {
                        // Form was already submitted, show thank you
                        // First set step to last step (needed for slideFormValue calculation)
                        const lastStepIndex = form.steps.length - 1;
                        dispatch({
                            type: ActionTypes.SET_STEP,
                            payload: lastStepIndex,
                        });
                        // Then mark as submitted
                        dispatch({
                            type: ActionTypes.SET_FORM_SUBMITTED,
                            payload: { formId: form.id },
                        });
                        setFormSubmitted(true);
                        return;
                    }

                    // Not submitted - sync to page form's step
                    const storedStepStr = sessionStorage.getItem(
                        syncStorageKeys.step,
                    );

                    const storedStep =
                        storedStepStr !== null
                            ? parseInt(storedStepStr, 10)
                            : 0;

                    // Only dispatch if step > 0 (initial state is already 0)
                    if (!isNaN(storedStep) && storedStep > 0) {
                        dispatch({
                            type: ActionTypes.SET_STEP,
                            payload: storedStep,
                        });
                    } else {
                        sessionStorage.setItem(syncStorageKeys.step, String(0));
                    }
                }, 0);
            }

            if (isFormInsideOffersModal) {
                const firstStepFields = form.steps[0].fields;
                const answered = firstStepFields.every((field) => {
                    return !!sessionStorageFields?.[field.codeName]?.value;
                });
                if (answered) {
                    dispatch({
                        type: ActionTypes.SET_STEP,
                        payload: 1,
                    });
                }
            }
        };

        void asyncFunction();

        window.history.pushState("", document.title, `#step-1`);

        if (sessionStorage.getItem("skip-to-step")) {
            const step = Number(sessionStorage.getItem("skip-to-step"));
            dispatch({
                type: ActionTypes.SET_STEP,
                payload: step,
            });
            sessionStorage.removeItem("skip-to-step");
        }

        if (!isGeneralModalForm) {
            window.addEventListener("popstate", handleWindowBack);
            return () => {
                window.removeEventListener("popstate", handleWindowBack);
            };
        }
    }, []);

    useEffect(() => {
        window.addEventListener("beforeunload", sendDataToServerListener);
        return () => {
            window.removeEventListener(
                "beforeunload",
                sendDataToServerListener,
            );
        };
    }, []);

    const getForcedBuyersIds = () => {
        const match = window.location.search?.match(/[?&]buyersIds=([^&]*)/);
        return match ? match[1].split(",").map((item) => Number(item)) : null;
    };

    useEffect(() => {
        if (!state.operation?.operationId) return;

        if (state.operation?.loadingBuyers) {
            window.cancelledOperations = window.cancelledOperations ?? [];
            window.cancelledOperations.push(state.operation.operationId);
        }

        if (state.operation.key === "FETCH_BUYERS") {
            fetchFirstRoundBuyers(state.operation.operationId);
        } else if (state.operation.key === "ROUND_ONE") {
            void startFirstRoundBidding({
                operationId: state.operation.operationId,
                firstRoundBuyers: state.operation.firstRoundBuyers,
                secondRoundBuyers: state.operation.secondRoundBuyers,
                isOtpResolved: otp ? otp.isResolved : false,
            });
        }
    }, [state.operation?.operationId, state.operation?.key]);

    useEffect(() => {
        if (isPiiCompletionForm) {
            dispatch({
                type: ActionTypes.SET_OPERATION,
                payload: {
                    firstRoundBuyers:
                        piiCompletionData?.buyers?.map((buyer) => ({
                            ...buyer,
                            selected: true,
                        })) ?? [],
                    secondRoundBuyers: [],
                    loadingBuyers: false,
                    operationId: piiCompletionData?.operationId,
                    key: "FETCH_BUYERS",
                },
            });
        }
    }, []);

    useEffect(() => {
        dispatch({
            type: ActionTypes.SET_OPERATION,
            payload: {
                firstRoundBuyers: form.defaultBuyers.map((buyer) => ({
                    ...buyer,
                    selected: true,
                })),
                secondRoundBuyers: [],
                hasDomainConsent: true,
            },
        });
    }, [form.defaultBuyers]);

    return {
        handleFieldChange,
        handleFieldBlur,
        handlePrevStep,
        handleSubmit,
        handleOutsideFormSubmit,
        currentStep,
        steps,
        visitedSteps,
        fieldsData: fieldsState,
        isLoading: loadingForm,
        successMessage,
        jornayaIdElement,
        formError,
        formId: form.id,
        formErrors,
        formErrorsResolved,
        otp: {
            isRequired: otp.isRequired,
            isResolved: otp.isResolved,
            countDownTime: otp.countDown,
            handleSubmit: handleOtpSubmit,
            handleResend: handleOtpResend,
            handleEditPhoneNumber: handleOtpEditPhoneNumber,
            handleScrollFormTop,
        },
        formSubmitted: submitted,
        stepStatus: stepStatus,
        checkIfStepHasOneField,
        showExitModal,
        showRegulations,
        showCustomListingModal,
        setLoading,
        setHasRegulations,
        setShowCustomListingModal,
        showPiiCompletionOtherFields,
        setShowPiiCompletionOtherFields,
        showFormModal,
        toggleFormModal,
        listingSlug,
        toggleShowFormInExitModal,
        showFromInExitModal,
        handleCloseFormInsideExitModal,
        dispatch,
        startSecondRoundBidding,
        operation,
        globalLoading,
        apiCallLoading,
        reloadFormData,
        instanceId: instanceIdRef.current,
        isLastStep: currentStep === steps?.length - 1,
    };
};
